import AppHttpClient from '../api-manager/api/http-client';
import HttpPathsEnum from '../api-manager/api/http-paths.enum';
import { HttpResponse } from '../api-manager/api/http-response';
import { LoginRequestDto } from './dto/request/login-request.dto';
import { LoginResponseDto } from './dto/response/login-response.dto';
import uploadImageRequest from '../image-uploader/ImageUploader.api';
import { RegisterRequestDto } from './dto/request/register-request.dto';
import { RegisterResponseDto } from './dto/response/register-response.dto';
import { HttpErrorResponse } from '../api-manager/api/http-error-response';
import { HttpRequest, RequestType } from '../api-manager/api/http-request';
import { UpdateUserRequestDto } from './dto/request/update-user.request.dto';
import { ConfirmUserRequestDto } from './dto/request/confirm-user.request.dto';
import { UpdateUserResponseDto } from './dto/response/update-user.response.dto';
import { EditPasswordRequestDto } from './dto/request/edit-password.request.dto';
import { RefreshTokenRequestDto } from './dto/request/refresh-token.request.dto';
import { ConfirmPhoneRequestDto } from './dto/request/confirm-phone.request.dto';
import { LoginWithMfaRequestDto } from './dto/request/login-with-mfa-request.dto';
import { LoginByTokenRequestDto } from './dto/request/login-by-token-request.dto';
import { ResetPasswordRequestDto } from './dto/request/reset-password-request.dto';
import { RefreshTokenResponseDto } from './dto/response/refresh-token.response.dto';
import { GetUserByIdResponseDto } from './dto/response/get-user-by-id.response.dto';
import { ChangePasswordRequestDto } from './dto/request/change-password.request.dto';
import { LoginByTokenResponseDto } from './dto/response/login-by-token-response.dto';
import { ConfirmPhoneCodeRequestDto } from './dto/request/confirm-phone-code.request.dto';
import { UpdateUserTermsDto } from './dto/request/update-user-terms.dto';

const USERS_PATH = HttpPathsEnum.USERS;

class AuthApi {
  public async register(data: RegisterRequestDto) {
    const request: HttpRequest<RegisterRequestDto> = {
      url: `${USERS_PATH}/auth/register`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<RegisterRequestDto, RegisterResponseDto>(
      request,
      RegisterResponseDto
    );
  }

  public async login(
    data: LoginRequestDto
  ): Promise<HttpResponse<LoginResponseDto>> {
    const request: HttpRequest<LoginRequestDto> = {
      url: `${USERS_PATH}/auth/login`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<LoginRequestDto, LoginResponseDto>(
      request,
      LoginResponseDto
    );
  }

  public async loginWithMFA(data: LoginWithMfaRequestDto) {
    const request: HttpRequest<LoginWithMfaRequestDto> = {
      url: `${USERS_PATH}/auth/confirm-mfa`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<LoginWithMfaRequestDto, LoginResponseDto>(
      request,
      LoginResponseDto
    );
  }

  public async changePassword(data: ChangePasswordRequestDto) {
    const request: HttpRequest<ChangePasswordRequestDto> = {
      url: `${USERS_PATH}/auth/force-change-password`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<ChangePasswordRequestDto, LoginResponseDto>(
      request,
      LoginResponseDto,
      { noAccess: true }
    );
  }

  public async isForceActive(email: string) {
    const request: HttpRequest<boolean> = {
      url: `${USERS_PATH}/auth/is-force-change-password/${email}`,
      method: RequestType.GET,
    };

    return await AppHttpClient.exec<any, boolean>(request, Boolean, {
      noAccess: true,
    });
  }

  public async editPassword(data: EditPasswordRequestDto) {
    const request: HttpRequest<EditPasswordRequestDto> = {
      url: `${USERS_PATH}/auth/password`,
      method: RequestType.PUT,
      data,
    };

    return await AppHttpClient.exec<EditPasswordRequestDto, string>(
      request,
      undefined
    );
  }

  public async approveTerms(data: UpdateUserTermsDto) {
    const request: HttpRequest<UpdateUserTermsDto> = {
      url: `${USERS_PATH}/auth/terms`,
      method: RequestType.PUT,
      data,
    };

    return await AppHttpClient.exec<UpdateUserTermsDto, LoginResponseDto>(
      request,
      undefined
    );
  }

  public async loginByToken() {
    const request: HttpRequest = {
      url: `${USERS_PATH}/auth/verify-token`,
      method: RequestType.GET,
    };

    return await AppHttpClient.exec<any, LoginByTokenResponseDto>(
      request,
      LoginByTokenResponseDto
    );
  }

  public async generateResetCode(email: string) {
    const request: HttpRequest = {
      url: `${USERS_PATH}/auth/generate-reset-code?email=${email}`,
      method: RequestType.GET,
    };

    return await AppHttpClient.exec<LoginByTokenRequestDto, boolean>(
      request,
      Boolean
    );
  }

  public async resetPassword(data: ResetPasswordRequestDto) {
    const request: HttpRequest = {
      url: `${USERS_PATH}/auth/reset-password?email=${data.email}&code=${data.code}&newPassword=${data.newPassword}`,
      method: RequestType.PATCH,
      data,
    };

    return await AppHttpClient.exec<LoginByTokenRequestDto, boolean>(
      request,
      Boolean
    );
  }

  public async refreshToken(data: RefreshTokenRequestDto) {
    const request: HttpRequest = {
      url: `${USERS_PATH}/auth/refresh-token`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<RefreshTokenRequestDto, LoginResponseDto>(
      request,
      RefreshTokenResponseDto,
      { noAccess: true }
    );
  }

  public async getMyProfile() {
    const request: HttpRequest = {
      url: `${USERS_PATH}/users/my-profile`,
      method: RequestType.GET,
    };

    return await AppHttpClient.exec<any, GetUserByIdResponseDto>(
      request,
      GetUserByIdResponseDto
    );
  }

  public async updateMyProfile(data: UpdateUserRequestDto) {
    const request: HttpRequest = {
      url: `${USERS_PATH}/users/update`,
      method: RequestType.PATCH,
      data,
    };

    return await AppHttpClient.exec<
      UpdateUserRequestDto,
      UpdateUserResponseDto
    >(request, UpdateUserResponseDto);
  }

  public async uploadUserImage(data: any) {
    return await uploadImageRequest(`${USERS_PATH}/upload/upload-image`, data);
  }

  public async confirmUser(data: ConfirmUserRequestDto) {
    const request: HttpRequest<ConfirmUserRequestDto> = {
      url: `${USERS_PATH}/auth/confirm-user`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<ConfirmUserRequestDto, boolean>(
      request,
      Boolean
    );
  }

  public async getPhoneConfirmationCode(data: ConfirmPhoneRequestDto) {
    const request: HttpRequest<ConfirmPhoneRequestDto> = {
      url: `${USERS_PATH}/auth/send-phone-verification-code`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<ConfirmPhoneRequestDto, boolean>(
      request,
      Boolean
    );
  }

  public async verifyPhone(data: ConfirmPhoneCodeRequestDto) {
    const request: HttpRequest<ConfirmPhoneCodeRequestDto> = {
      url: `${USERS_PATH}/auth/verify-phone`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<
      ConfirmPhoneCodeRequestDto,
      LoginResponseDto
    >(request, LoginResponseDto);
  }
}

export default new AuthApi();
